/* ============================================
   Globdrop — Game Engine (Finite State Machine)
   ============================================ */

import type { GameSession, GameState, GameMode, RoundResult, Scenario } from '../types/scenario.ts';
import { calculateDistance } from './coordinate-math.ts';
import { calculatePoints } from './scoring.ts';
import type { PixelPoint, ImageSize } from '../types/scenario.ts';

/**
 * Core game engine implementing a Finite State Machine.
 *
 * States: IDLE → SETUP → COUNTDOWN → PLAYING → FEEDBACK → RESULTS
 *                                       ↑          │
 *                                       └──────────┘ (next round)
 */
export class GameEngine {
  private session: GameSession;
  private usedPlaceIndices: Set<number> = new Set();

  constructor() {
    this.session = this.createEmptySession();
  }

  // ── Public API ──

  getSession(): Readonly<GameSession> {
    return this.session;
  }

  /** Select a scenario and move to SETUP */
  selectScenario(scenario: Scenario): void {
    this.session.scenario = scenario;
    this.transition('SETUP');
  }

  /** Select a mode and start the game */
  startGame(mode: GameMode): void {
    this.session.mode = mode;
    this.session.currentRound = 0;
    this.session.totalRounds = Math.min(mode.rounds, this.session.scenario!.places.length);
    this.session.rounds = [];
    this.session.totalPoints = 0;
    this.session.totalKm = 0;
    this.usedPlaceIndices.clear();
    this.transition('COUNTDOWN');
    this.pickNextPlace();
  }

  /** Called when the countdown finishes — start playing */
  countdownDone(): void {
    this.transition('PLAYING');
    this.session.timeRemaining = this.session.mode!.timePerRound;
  }

  /** Update time remaining (called by timer tick) */
  updateTime(remaining: number): void {
    this.session.timeRemaining = remaining;
  }

  /** Handle user click on the map */
  handleMapClick(
    userPixel: PixelPoint,
    imageSize: ImageSize
  ): RoundResult {
    const { scenario, timeRemaining } = this.session;
    const place = this.session.currentPlace!;

    const distanceKm = calculateDistance(
      userPixel,
      { x: place.x, y: place.y },
      scenario!.boundingBox,
      imageSize,
      scenario!.mapRealWidth,
      scenario!.coordinateSystem,
      scenario!.distanceUnit
    );

    const points = calculatePoints(
      distanceKm,
      timeRemaining,
      scenario!.maxDistance || 50
    );

    const result: RoundResult = {
      place,
      distanceKm: Math.round(distanceKm * 10) / 10,
      points,
      timeRemaining,
      clicked: true,
    };

    this.recordRound(result);
    return result;
  }

  /** Handle timeout (no click) */
  handleTimeout(): RoundResult {
    const place = this.session.currentPlace!;

    const result: RoundResult = {
      place,
      distanceKm: 0,
      points: 0,
      timeRemaining: 0,
      clicked: false,
    };

    this.recordRound(result);
    return result;
  }

  /** Move to the next round or end the game */
  nextRound(): void {
    if (this.session.currentRound >= this.session.totalRounds) {
      this.transition('RESULTS');
    } else {
      this.pickNextPlace();
      this.transition('COUNTDOWN');
    }
  }

  /** Go back to IDLE (home) */
  goHome(): void {
    this.session = this.createEmptySession();
    this.transition('IDLE');
  }

  /** Replay with same scenario and mode */
  replay(): void {
    if (this.session.scenario && this.session.mode) {
      this.startGame(this.session.mode);
    }
  }

  // ── Private ──

  private transition(newState: GameState): void {
    this.session.state = newState;
  }

  private pickNextPlace(): void {
    const places = this.session.scenario!.places;
    let index: number;

    // Pick a random place that hasn't been used yet
    do {
      index = Math.floor(Math.random() * places.length);
    } while (this.usedPlaceIndices.has(index) && this.usedPlaceIndices.size < places.length);

    this.usedPlaceIndices.add(index);
    this.session.currentPlace = places[index]!;
  }

  private recordRound(result: RoundResult): void {
    this.session.rounds.push(result);
    this.session.totalPoints += result.points;
    this.session.totalKm += result.distanceKm;
    this.session.currentRound++;
    this.transition('FEEDBACK');
  }

  private createEmptySession(): GameSession {
    return {
      scenario: null,
      mode: null,
      state: 'IDLE',
      currentRound: 0,
      totalRounds: 0,
      rounds: [],
      totalPoints: 0,
      totalKm: 0,
      currentPlace: null,
      timeRemaining: 0,
    };
  }
}
